import classNames from 'classnames';
import React, {useMemo, useCallback, useState, useContext} from 'react';

import {BrowseCourse} from '@udemy/browse-course';
import {InDemandCareer} from '@udemy/career-academies';
import {AddToCart, AddToCartProps, BundleType, CartBuyable} from '@udemy/cart';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {Button} from '@udemy/react-core-components';
import {ToasterStore} from '@udemy/react-messaging-components';
import {Loader} from '@udemy/react-reveal-components';
import {SubscriptionCTA} from '@udemy/subscription-browse';
import {useConsumerSubscriptionPlan} from '@udemy/subscription-graphql';
import {udLink} from '@udemy/ud-data';

import {useEnrollInAcceleratorMutation} from 'src/generated/graphql';

import {CareerAcceleratorCTAType, CTAContext} from '../cta-context/cta-context';
import styles from './post-cta-experience-button.module.less';

interface PostCtaExperienceProps extends AddToCartProps {
    academy: InDemandCareer;
    courses: BrowseCourse[];
}

export const PostCtaExperienceButton = ({academy, courses, ...props}: PostCtaExperienceProps) => {
    const personalPlanContext = useConsumerSubscriptionPlan();
    const [isEnrollmentLoading, setIsEnrollmentLoading] = useState(false);
    const {gettext} = useI18n();
    const isWiderThanMdMin = useMatchMedia('md-min');
    const {ctaType} = useContext(CTAContext);

    const isLoading = ctaType === CareerAcceleratorCTAType.LOADING || isEnrollmentLoading;

    const enrollCtaClickHandler = useCallback(async () => {
        setIsEnrollmentLoading(true);
        try {
            await useEnrollInAcceleratorMutation.fetcher({id: academy.numericId.toString()})();
            window.location.href = udLink.to('/home/my-courses/career-accelerators/');
        } catch (e) {
            setIsEnrollmentLoading(false);
            ToasterStore.addAlertBannerToast({
                udStyle: 'error',
                title: gettext('Error enrolling in Career Accelerator'),
                body: 'Please try again.',
                actionButtonProps: false,
            });
        }
    }, [gettext, academy.numericId]);

    const CTA = useMemo(() => {
        if (isLoading) {
            return (
                <Button size={!isWiderThanMdMin ? 'medium' : 'large'} disabled={true}>
                    <Loader />
                </Button>
            );
        }

        switch (ctaType) {
            case CareerAcceleratorCTAType.GO_TO_COURSES_MX: {
                return (
                    <Button
                        size={!isWiderThanMdMin ? 'medium' : 'large'}
                        componentClass="a"
                        href={udLink.to('/home/my-courses/learning/')}
                        target="_self"
                    >
                        {gettext('Go to courses')}
                    </Button>
                );
            }

            case CareerAcceleratorCTAType.GO_TO_COURSES_PP: {
                return (
                    <Button
                        size={!isWiderThanMdMin ? 'medium' : 'large'}
                        componentClass="a"
                        href={udLink.to('/home/my-courses/career-accelerators/')}
                        target="_self"
                    >
                        {gettext('Go to courses')}
                    </Button>
                );
            }

            case CareerAcceleratorCTAType.ENROLL_IN_COURSES: {
                return (
                    <Button
                        onClick={enrollCtaClickHandler}
                        size={!isWiderThanMdMin ? 'medium' : 'large'}
                        target="_self"
                    >
                        {gettext('Enroll in courses')}
                    </Button>
                );
            }

            case CareerAcceleratorCTAType.PURCHASE_CTA_PP: {
                return (
                    <SubscriptionCTA
                        size={!isWiderThanMdMin ? 'medium' : 'large'}
                        plan={personalPlanContext}
                        bundleId={academy.slug}
                        bundleType={'career-academy'}
                        uiRegion={props.uiRegion ?? 'career-academy-subs-cta'}
                        showSubtitle={false}
                        trialCtaText={gettext('Get started for free')}
                        source={{type: 'career-accelerator', id: academy.id}}
                    />
                );
            }

            case CareerAcceleratorCTAType.PURCHASE_CTA_MX: {
                return (
                    <AddToCart
                        size={!isWiderThanMdMin ? 'medium' : 'large'}
                        buyables={courses as CartBuyable[]}
                        cartButtonTextAdd={gettext('Get started')}
                        cartButtonTextGoToCart={gettext('Get started')}
                        forceGoToCart={true}
                        bundleType={BundleType.CAREER_ACADEMY}
                        bundleId={academy.slug}
                        {...props}
                    />
                );
            }
        }
    }, [
        academy,
        courses,
        ctaType,
        enrollCtaClickHandler,
        gettext,
        isLoading,
        isWiderThanMdMin,
        personalPlanContext,
        props,
    ]);

    return <div className={classNames(styles['add-to-cart-container'])}>{CTA}</div>;
};
